/**
 * v0M2.8 spike — Canvas 2D render profile.
 *
 * Instrumented render sweep for the last v0M2 architectural gate.
 * Activated via `?profile=1` URL flag in `main.ts`; takes over the
 * render loop and drives a scripted sweep over the three zoom steps
 * (2.5 / 4 / 8 px/cell — see camera.ts).
 *
 * Per zoom step, we measure:
 *   - Bake ms (one-shot per zoom change; MAP.md §2.4a / §2.6c rebake)
 *   - Draw ms (per frame, stationary; N=60 samples)
 *   - Draw ms (per frame, panning; N=60 samples)
 *
 * At the end we emit a console.table summary + expose a download link
 * for the JSON snapshot at `docs/SPIKES/v0M2.8-latest.json` (browser-
 * scoped — Michael saves it manually).
 *
 * Retires CLAUDE.md §6 #3 (Canvas 2D vs WebGL renderer redirect). If
 * numbers stay in-band, Canvas 2D is confirmed as the shipping
 * architecture at 480×280 grand-map scale with tile atlas + hillshading
 * + rivers + borders + city icons + HUD.
 */

import type { LoadedMap } from './map/map-loader';
import type { TileAtlas } from './map/tile-atlas';
import type { Camera } from './map/camera';
import { bakeAllLayers, drawFrame } from './map/renderer';
import { pxPerCell, ZOOM_STEPS } from './map/camera';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/**
 * Frames per stationary and per panning measurement window. 60 gives
 * ~1s of samples at 60 fps — enough for stable p50/p95 without dragging
 * the sweep out.
 */
const STATIONARY_FRAMES = 60;
const PAN_FRAMES = 60;

/**
 * Per-frame pan in world cells during the panning window. 0.4
 * cells/frame at zoom 4 px/cell = ~1.6 screen-px/frame — a slow-pan
 * feel that still touches new terrain each frame and forces the drawn
 * region to shift.
 */
const PAN_CELLS_PER_FRAME = 0.4;

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

interface FrameStats {
  mean: number;
  p50: number;
  p95: number;
  max: number;
  dropped60fps: number;
  count: number;
}

interface ZoomStepResult {
  zoomStep: number;
  pxPerCell: number;
  bakeMs: number;
  stationary: FrameStats;
  panning: FrameStats;
}

interface ProfileSnapshot {
  runAt: string;
  userAgent: string;
  canvasWidth: number;
  canvasHeight: number;
  mapDims: [number, number];
  results: ZoomStepResult[];
}

// ---------------------------------------------------------------------------
// Stats helpers
// ---------------------------------------------------------------------------

function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * p));
  return sorted[idx]!;
}

function summarise(samples: readonly number[]): FrameStats {
  if (samples.length === 0) {
    return { mean: 0, p50: 0, p95: 0, max: 0, dropped60fps: 0, count: 0 };
  }
  const sorted = [...samples].sort((a, b) => a - b);
  const sum = samples.reduce((a, v) => a + v, 0);
  return {
    mean: sum / samples.length,
    p50: percentile(sorted, 0.5),
    p95: percentile(sorted, 0.95),
    max: sorted[sorted.length - 1]!,
    dropped60fps: samples.filter((v) => v > 16.67).length,
    count: samples.length,
  };
}

function fmt(n: number, digits = 2): string {
  return n.toFixed(digits);
}

// ---------------------------------------------------------------------------
// Instrumented bake / draw wrappers
// ---------------------------------------------------------------------------

function measureBake(
  map: LoadedMap,
  camera: Camera,
  atlas: TileAtlas | null,
): { baked: ReturnType<typeof bakeAllLayers>; ms: number } {
  const t0 = performance.now();
  const baked = bakeAllLayers(map, camera, atlas);
  const ms = performance.now() - t0;
  return { baked, ms };
}

function measureDraw(
  ctx: CanvasRenderingContext2D,
  map: LoadedMap,
  camera: Camera,
  baked: ReturnType<typeof bakeAllLayers>,
): number {
  const t0 = performance.now();
  drawFrame(ctx, map, camera, baked);
  return performance.now() - t0;
}

/** Wait one animation frame. Returns the rAF timestamp. */
function nextFrame(): Promise<number> {
  return new Promise((resolve) => requestAnimationFrame(resolve));
}

// ---------------------------------------------------------------------------
// Sweep driver
// ---------------------------------------------------------------------------

/**
 * Runs the full v0M2.8 sweep: for each zoom step, one bake sample,
 * then STATIONARY_FRAMES draw samples with the camera parked at map
 * centre, then PAN_FRAMES draw samples while panning east. Yields to
 * rAF between each frame so the browser can actually paint.
 */
async function runSweep(
  ctx: CanvasRenderingContext2D,
  map: LoadedMap,
  atlas: TileAtlas | null,
  camera: Camera,
): Promise<ZoomStepResult[]> {
  const results: ZoomStepResult[] = [];

  for (let z = 0; z < ZOOM_STEPS.length; z++) {
    camera.zoomStep = z;
    camera.centerX = map.width / 2;
    camera.centerY = map.height / 2;

    // eslint-disable-next-line no-console
    console.info(`[profile] zoom step ${z} (${ZOOM_STEPS[z]!} px/cell) — baking ...`);
    const { baked, ms: bakeMs } = measureBake(map, camera, atlas);
    // Draw one frame so the user sees the current zoom during the sweep.
    drawFrame(ctx, map, camera, baked);
    await nextFrame();

    // Stationary window
    const stationarySamples: number[] = [];
    for (let i = 0; i < STATIONARY_FRAMES; i++) {
      await nextFrame();
      stationarySamples.push(measureDraw(ctx, map, camera, baked));
    }

    // Panning window — slide east one small step per frame.
    const panSamples: number[] = [];
    for (let i = 0; i < PAN_FRAMES; i++) {
      camera.centerX += PAN_CELLS_PER_FRAME;
      // Bounce off the eastern edge so we stay on-map.
      if (camera.centerX > map.width - 4) {
        camera.centerX = 4;
      }
      await nextFrame();
      panSamples.push(measureDraw(ctx, map, camera, baked));
    }

    results.push({
      zoomStep: z,
      pxPerCell: pxPerCell(camera),
      bakeMs,
      stationary: summarise(stationarySamples),
      panning: summarise(panSamples),
    });
  }

  return results;
}

// ---------------------------------------------------------------------------
// Reporting
// ---------------------------------------------------------------------------

function printReport(snapshot: ProfileSnapshot): void {
  // eslint-disable-next-line no-console
  console.info(
    '%c===== v0M2.8 spike — Canvas 2D render profile =====',
    'color:#e6dcc4;background:#0e0b06;padding:2px 6px;border-radius:2px;font-weight:bold',
  );
  // eslint-disable-next-line no-console
  console.info(
    `Canvas: ${snapshot.canvasWidth}×${snapshot.canvasHeight} · Map: ${snapshot.mapDims[0]}×${snapshot.mapDims[1]} · UA: ${snapshot.userAgent}`,
  );

  const rows: Record<string, string>[] = [];
  for (const r of snapshot.results) {
    rows.push({
      Zoom: `step ${r.zoomStep} (${r.pxPerCell} px/cell)`,
      'Bake ms': fmt(r.bakeMs, 1),
      'Stat mean': fmt(r.stationary.mean),
      'Stat p50': fmt(r.stationary.p50),
      'Stat p95': fmt(r.stationary.p95),
      'Stat max': fmt(r.stationary.max),
      'Stat dropped': `${r.stationary.dropped60fps}/${r.stationary.count}`,
      'Pan mean': fmt(r.panning.mean),
      'Pan p50': fmt(r.panning.p50),
      'Pan p95': fmt(r.panning.p95),
      'Pan max': fmt(r.panning.max),
      'Pan dropped': `${r.panning.dropped60fps}/${r.panning.count}`,
    });
  }
  // eslint-disable-next-line no-console
  console.table(rows);

  // eslint-disable-next-line no-console
  console.info(
    '%cPass/fail bands (per v0M2.8 plan) — p95 draw ms:',
    'font-weight:bold',
  );
  // eslint-disable-next-line no-console
  console.info('  Zoom-out (step 0, 2.5 px/cell): pass < 8 (125 fps) / concern 8-16 / fail > 16 (60 fps floor)');
  // eslint-disable-next-line no-console
  console.info('  Zoom-mid  (step 1, 4 px/cell) : pass < 6 / concern 6-16 / fail > 16');
  // eslint-disable-next-line no-console
  console.info('  Zoom-in   (step 2, 8 px/cell) : pass < 6 / concern 6-16 / fail > 16');
  // eslint-disable-next-line no-console
  console.info('  Bake per zoom change          : pass < 100 (instant) / concern 100-500 / fail > 500');
  // eslint-disable-next-line no-console
  console.info('  Pan dropped frames (60-frame window): pass 0 / concern 1-3 / fail > 3');
}

function offerDownload(snapshot: ProfileSnapshot): void {
  const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const banner = document.createElement('div');
  banner.style.cssText = [
    'position:fixed',
    'top:16px',
    'left:50%',
    'transform:translateX(-50%)',
    'background:#0e0b06',
    'color:#e6dcc4',
    'padding:12px 20px',
    'border:1px solid #c9a24e',
    'border-radius:4px',
    'font-family:"SF Mono", Menlo, Consolas, monospace',
    'font-size:13px',
    'z-index:9999',
    'box-shadow:0 4px 12px rgba(0,0,0,0.5)',
  ].join(';');
  banner.innerHTML = `
    v0M2.8 profile sweep complete —
    <a href="${url}" download="v0M2.8-latest.json" style="color:#c9a24e;text-decoration:underline">Download JSON snapshot</a>
    &nbsp;·&nbsp; Table printed to console.
  `;
  document.body.appendChild(banner);
}

// ---------------------------------------------------------------------------
// Public entry
// ---------------------------------------------------------------------------

/**
 * Run the v0M2.8 sweep in place of the normal render loop.
 * Call once from main.ts when `?profile=1` is set.
 */
export async function runRenderProfile(args: {
  ctx: CanvasRenderingContext2D;
  map: LoadedMap;
  atlas: TileAtlas | null;
  camera: Camera;
}): Promise<void> {
  const { ctx, map, atlas, camera } = args;

  // eslint-disable-next-line no-console
  console.info(
    '%c[profile] v0M2.8 render sweep starting …',
    'color:#c9a24e;font-weight:bold',
  );

  const results = await runSweep(ctx, map, atlas, camera);

  const snapshot: ProfileSnapshot = {
    runAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    canvasWidth: ctx.canvas.width,
    canvasHeight: ctx.canvas.height,
    mapDims: [map.width, map.height],
    results,
  };

  printReport(snapshot);
  offerDownload(snapshot);
}
